import { useMemo, useState } from 'react';
import { Grid, Typography, Button, Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import ProductConfigurationIcon from 'assets/icons/product-configuration-icon.svg?react';
import BackToWorkflowOverviewIcon from 'assets/icons/return-workflow-overview-icon-blue.svg?react';
import { jbColors } from 'theme/jbColors';

import TaskOverview from 'components/productDetails/TaskOverview';
import AccordionGridWrapper from 'components/common/AccordionGridWrapper';
import StickyHeaderWrapper from 'components/common/StickyHeaderWrapper';
import ProductConfigurationView from './ProductConfigurationView';
import { MessageBox } from 'components/common/MessageBox';
import { getPartnerName } from 'utils/stringUtils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import OpenTask from './OpenTask';
import { StepMetadata } from 'pco-product-service-internal-consumer';
import { ServiceRole } from 'pco-product-service-internal-consumer';
import { Task } from 'pco-product-service-internal-consumer';
import RightSideBottomBar from 'components/common/RightSideBottomBar';
import { deactivateServiceRole } from 'thunks/serviceRoleThunk';
import CommonDialog from 'components/common/CommonDialog';
import CloseIcon from 'assets/icons/close-icon.svg?react';
import HamburgerIcon from 'assets/icons/hamburger-icon.svg?react';
import { determineStatusFromEnum, StepMetadataType } from 'types';
import Documents from './documents/Documents';
import { selectBrUid } from 'selectors/accountSelector';
import { selectPartnerId } from 'selectors/partnerStatusSelector';
import { LoadingSpinner } from 'components/loadingSpinner/LoadingSpinner';

const ServiceRoleDetails = () => {
  const navigate = useNavigate();
  const serviceRoleUid = useParams().serviceRoleUid;
  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);
  const serviceRole: ServiceRole | undefined = selectedServiceAgreement?.serviceRoles?.filter(
    (serviceRole) => serviceRole.serviceRoleUid === serviceRoleUid,
  )[0];
  const brUid = useSelector(selectBrUid);
  const partnerUid = useSelector(selectPartnerId);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { openTask, stepMetadata } = useMemo<{
    openTask: Task | undefined;
    stepMetadata: StepMetadata | undefined;
  }>(() => {
    const taskInProgress = serviceRole?.stepMetadata?.filter((t) => t !== undefined)[0];
    if (taskInProgress && serviceRole?.stepMetadata) {
      return {
        openTask: { taskUid: taskInProgress?.techId ?? '', taskName: taskInProgress?.taskName },
        stepMetadata: serviceRole.stepMetadata[0],
      };
    }
    return { openTask: undefined, stepMetadata: undefined };
  }, [serviceRole]);
  const { loader } = useSelector((state: RootState) => state.appConfig);
  const dispatch = useDispatch<AppDispatch>();

  const mappedServiceRole = {
    'Domicile Country': serviceRole?.domicileCountry ?? undefined,
    'Partner no.': serviceRole?.partnerNumber ?? undefined,
    Name: getPartnerName(serviceRole) ?? undefined,
    'Valid From - Valid To': serviceRole?.validFrom + ' - ' + (serviceRole?.validTo ?? ''),
    Justification: serviceRole?.justification ?? undefined,
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderStatusMessages = () => {
    return serviceRole?.statuses?.map((status) => {
      return <MessageBox key={status.message} message={status.message ?? ''} messageType="info" hasBorder={true} />;
    });
  };

  const handleDeactivateServiceRole = async (serviceRoleUid: string | undefined) => {
    if (serviceRoleUid) dispatch(deactivateServiceRole(serviceRoleUid));

    if (partnerUid) {
      navigate(`/partner/${partnerUid}/serviceAgreement/${selectedServiceAgreement?.serviceAgreementId}/details`);
    } else {
      navigate(`/accounts/${brUid}/serviceAgreement/${selectedServiceAgreement?.serviceAgreementId}/details`);
    }
    setIsOpen(false);
  };

  const taskUid = serviceRole?.stepMetadata?.find(
    (x) => x?.stepMetadataType === StepMetadataType.PREAPPROVAL_FORM_METADATA,
  )?.taskId;

  return loader?.loading ? (
    <LoadingSpinner message={loader.message} />
  ) : (
    <>
      <StickyHeaderWrapper
        hasStickyBottomBar={true}
        hasOpenTask={true}
        side="right"
        topComponents={
          <>
            <Grid
              item
              xs={12}
              data-testid={'service-role-details'}
              sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}
            >
              <Typography variant="h2">Service Role Details</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                ml: 0.5,
                mb: 2.5,
                mt: 3.5,
              }}
            >
              <Grid
                container
                item
                xs={3}
                onClick={handleBack}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 1,
                  mt: 1,
                  ml: -0.6,
                  width: 'fit-content',
                  border: `1px solid ${jbColors.core.stone[30]}`,
                  borderBottom: 'none',
                  pb: 1,
                  backgroundColor: 'white',
                  ':hover': {
                    backgroundColor: jbColors.core.stone[40],
                    color: jbColors.core.reflexBlue[100],
                    border: `1px solid ${jbColors.core.stone[30]}`,
                  },
                }}
              >
                <Grid item xs={1}>
                  <BackToWorkflowOverviewIcon
                    style={{
                      display: 'flex',
                      color: jbColors.core.reflexBlue[100],
                      height: '1em',
                      width: '0.6em',
                      marginRight: '0.5em',
                    }}
                  />
                </Grid>
                <Grid item xs={11}>
                  <Typography variant={'body2'} sx={{ color: jbColors.core.reflexBlue[100] }}>
                    {`Return to product's details`}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </>
        }
        centerComponents={
          <>
            {selectedServiceAgreement && (
              <AccordionGridWrapper
                expandedProp={true}
                showExpandIcon={false}
                customCloseAction={true}
                title={
                  selectedServiceAgreement?.serviceDefinitionName
                    ? `${selectedServiceAgreement.serviceDefinitionName}`
                    : ''
                }
                titleIcon={<ProductConfigurationIcon />}
              >
                {openTask && stepMetadata && (
                  <OpenTask
                    isServiceRoleDetails={true}
                    selectedServiceRole={serviceRole ?? undefined}
                    stepMetadata={stepMetadata}
                    taskUid={taskUid}
                  />
                )}
              </AccordionGridWrapper>
            )}
            {serviceRole?.tasks?.length !== 0 && (
              <AccordionGridWrapper title={'Task Overview'} titleIcon={<ProductConfigurationIcon />}>
                <TaskOverview tasks={serviceRole?.tasks ?? []} />
              </AccordionGridWrapper>
            )}
            {/* TODO MARK ->   */}
            <AccordionGridWrapper titleIcon={<ProductConfigurationIcon />} title={'Service Role Details'}>
              <ProductConfigurationView title={''} attributes={mappedServiceRole} />
            </AccordionGridWrapper>

            <AccordionGridWrapper titleIcon={<ProductConfigurationIcon />} title={'Eligibility Criteria'}>
              {!serviceRole?.isEligible ? (
                <>
                  <MessageBox
                    message={
                      'Delegate eligibility criteria are not met. Please check the details below and address the issues in the partner profile.'
                    }
                    messageType="warning"
                    hasBorder={false}
                  />
                  {renderStatusMessages()}
                </>
              ) : (
                <MessageBox
                  message={'All delegate eligibility criteria were met'}
                  messageType="success"
                  hasBorder={false}
                />
              )}
            </AccordionGridWrapper>

            {serviceRole?.documents?.length !== 0 && (
              <AccordionGridWrapper title={'Documents'}>
                <Documents documents={serviceRole?.documents ?? []} />
              </AccordionGridWrapper>
            )}
          </>
        }
      />

      <CommonDialog
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        title={'Deactivate Delegate'}
        titleIcon={<HamburgerIcon />}
        closeIcon={<CloseIcon onClick={() => setIsOpen(false)} />}
        removeCancel={true}
        content={
          <Grid container>
            <Grid item xs={12} sx={{ mt: 1, mb: 2 }}>
              <Typography variant="body1">
                Please confirm that you would like to deactivate this service role. Upon confirmation this change cannot
                be undone. Please ensure that affected counterparty is notified about the change
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleDeactivateServiceRole(serviceRoleUid)}
                  sx={{ marginRight: '10px', backgroundcolor: 'white' }}
                  disabled={!serviceRole?.canBeDeactivated}
                >
                  Deactivate Delegate
                </Button>
              </Box>
            </Grid>
          </Grid>
        }
      />

      <RightSideBottomBar>
        {serviceRole?.serviceRoleDefinition.canBeRemoved && (
          <Button
            sx={{
              color: 'white',
              backgroundColor: jbColors.core.reflexBlue[100],
              ':hover': {
                color: jbColors.core.reflexBlue[100],
                backgroundColor: 'white',
              },
            }}
            disabled={
              serviceRole?.status === 'DELETED' ||
              determineStatusFromEnum(selectedServiceAgreement?.agreementStatus) === 'Inactive' ||
              !serviceRole?.canBeDeactivated
            }
            variant="outlined"
            onClick={() => setIsOpen(true)}
          >
            Deactivate Delegate
          </Button>
        )}
      </RightSideBottomBar>
    </>
  );
};

export default ServiceRoleDetails;
